import React, { useCallback, useContext, useMemo } from 'react';

import { Button, Col, Row, Tag, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';

import useSwr from '@hooks/useSwr';
import { RoamingChartData } from '@services/types/Roaming/tableData';
import useSearchParams from '@hooks/useSearchParams';
import { ClearOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';
import { ChartComponent } from './ChartComponent';
import { BarComponent } from './BarComponent';
import { SecondBarComponent } from './SecondBarComponent';

type ChartWithContentProps = {
  isRoot: boolean;
};

const ChartWithContent = ({ isRoot }: ChartWithContentProps) => {
  const { t: translate } = useTranslation('roaming');
  const { theme } = useContext(SessionContext);
  const {
    setSearchParams,
    clearSearchParams,
    searchParams,
    hasValuesInExtraParams,
  } = useSearchParams({
    type: undefined,
    extraParams: [
      'operator',
      'rangeRoaming',
      'customerName',
      'segment',
      'product',
      'cpfCnpj',
    ],
  });

  const { product, operator, rangeRoaming, customerName, segment, cpfCnpj } =
    useMemo(() => searchParams, [searchParams]);

  const filters: any = useMemo(() => {
    const allFilters = {
      product,
      operator,
      rangeRoaming,
      customerName,
      segment,
      cpfCnpj,
    };

    return allFilters;
  }, [product, operator, rangeRoaming, customerName, segment, cpfCnpj]);

  const { data: roamingChartData } = useSwr<RoamingChartData>(
    '/service-proxy/roaming/count',
    {
      params: {
        ...filters,
        product: product && `%${product.toUpperCase()}%`,
        customerName: customerName && `%${customerName.toUpperCase()}%`,
        cpfCnpj: cpfCnpj && `%${cpfCnpj}%`,
      },
    },
  );

  const handleOrdenateData = useCallback(
    (
      fieldToGetData:
        | 'countOperator'
        | 'countProduct'
        | 'countRangeRoaming'
        | 'countPhoneCustomer'
        | 'countSegment',
    ) => {
      if (roamingChartData) {
        const chartDataField = roamingChartData[fieldToGetData];
        chartDataField.sort(
          (item, secondItem) => Number(item.value) - Number(secondItem.value),
        );

        return [
          { value: 0 },
          ...chartDataField,
          {
            value:
              Number(chartDataField[chartDataField.length - 1]?.value) + 500,
          },
        ];
      }
      return [];
    },
    [roamingChartData],
  );
  const findRealName = useCallback(
    (titleToSearch: string, customer: boolean) => {
      const findItemCustomer = roamingChartData?.countPhoneCustomer.find(item =>
        item?.key?.includes(titleToSearch?.slice(0, -3)),
      );
      const findItemProduct = roamingChartData?.countProduct.find(item =>
        item?.key?.includes(titleToSearch?.slice(0, -3)),
      );
      return customer ? findItemCustomer : findItemProduct;
    },
    [roamingChartData?.countProduct, roamingChartData?.countPhoneCustomer],
  );

  const colorsByChart: any = {
    operator: '#FFC300',
    customerName: '#4BFF32',
    segment: '#00BEFF',
    product: '#28BEA5',
    rangeRoaming: '#239687',
  };

  const formatLabels = useCallback((label: string) => {
    return Number(label) <= 999
      ? label
      : `${(Number(label) / 1000).toFixed(2)}k`;
  }, []);

  return (
    <div>
      {hasValuesInExtraParams && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div>
            {Object.keys(filters).map(
              (item: any) =>
                filters[item] && (
                  <Tag
                    closable
                    onClose={() => clearSearchParams([item])}
                    key={item}
                    color={colorsByChart[item]}
                  >
                    {filters[item]}
                  </Tag>
                ),
            )}
          </div>
          <Tooltip title={translate('chart-titles.clear-filters')}>
            <Button
              onClick={() => clearSearchParams()}
              style={{ padding: '0 8px' }}
            >
              <ClearOutlined />
            </Button>
          </Tooltip>
        </div>
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col lg={isRoot ? 8 : 12} xs={24} style={{ height: '100%' }}>
          <ChartComponent
            dataChart={handleOrdenateData('countRangeRoaming')}
            formatLabels={formatLabels}
            setSearchParams={setSearchParams}
            title="chart-titles.telephones-per-day"
            color={theme?.colors?.green}
            field="rangeRoaming"
          />
        </Col>
        <Col lg={isRoot ? 8 : 12} xs={24} style={{ height: '100%' }}>
          <ChartComponent
            dataChart={handleOrdenateData('countOperator')}
            formatLabels={formatLabels}
            setSearchParams={setSearchParams}
            title="chart-titles.telephones-per-operator"
            color={theme?.colors?.yellow}
            field="operator"
          />
        </Col>

        {isRoot && (
          <Col lg={8} xs={24} style={{ height: '100%' }}>
            <ChartComponent
              dataChart={handleOrdenateData('countSegment')}
              formatLabels={formatLabels}
              setSearchParams={setSearchParams}
              title="chart-titles.telephones-per-segment"
              color={theme?.colors?.blue}
              field="segment"
            />
          </Col>
        )}
      </Row>

      {isRoot && (
        <Row gutter={[16, 16]}>
          <Col lg={12} xs={24} style={{ height: '100%' }}>
            <SecondBarComponent
              dataBar={handleOrdenateData('countPhoneCustomer')}
              formatLabels={formatLabels}
              setSearchParams={setSearchParams}
              title="chart-titles.telephones-per-customer"
              color={theme?.colors?.primary}
              field="customerName"
            />
          </Col>
          <Col lg={12} xs={24} style={{ height: '100%' }}>
            <BarComponent
              dataBar={handleOrdenateData('countProduct')}
              formatLabels={formatLabels}
              setSearchParams={setSearchParams}
              title="chart-titles.telephones-per-products"
              color={theme?.colors?.primary}
              field="product"
              findRealName={findRealName}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ChartWithContent;
